import { EmotionCharacterButton } from "./Controls/EmotionCharacterButton";
import * as classNames from "classnames";
import * as React from "react";
import { useFontSwitcher } from "source/app/presentation/Shared/Fonts/Hooks/useFontSwitcher";
import { Fonts } from "source/app/presentation/Shared/Styles/SharedStyles";
import { MathUtils } from "source/app/services/Math/MathUtils";
import { Emotion } from "../../../../domain/Emotions/Emotion";
import { Emotions } from "../../../../domain/Emotions/Emotions";
import { ImageWithOverlay } from "../../../Shared/Image/ImageWithOverlay";
import { ShieldOrnaments } from "./ShieldOrnaments";
import { StyleClass } from "../../../Shared/Styles/CSS";
import { webkitFilterGlitchFix } from "../../../Shared/Styles/SharedStyles";

export function InactiveEmotionCharacter({
    emotion,
    handleClick,
    handleLongPress,
}: {
    emotion: Emotion;
    handleClick: () => void;
    handleLongPress?: () => void;
}) {
    const [bottomPosition, setBottomPosition] = React.useState(
        -10 + emotion.Index * 1.5
    );
    const useAnopenscrollFont = useFontSwitcher();

    const sepia = emotion.IsAtLeast(Emotions.Integrated) ? 0 : 1;

    const shieldFilterCSS = emotion.shieldHueRotateDegrees
        ? `sepia(${sepia}) hue-rotate(${emotion.shieldHueRotateDegrees}deg) saturate(1.1) brightness(0.68) contrast(1.03) drop-shadow(0px 8px 4px rgba(0,0,0,0.45))`
        : ``;

    const imageOptions = {
        imageLabel: `inactiveEmotionCharacterShield_border_${emotion.character}`,
        imageWidth: 43,
        imageHeight: 68.8,
        imageUrl: "/images/icons/shield-gold-border.svg",
        imageCSS: {
            display: `inline-flex`,
            zIndex: 0,
            position: "relative" as "relative",
        },
    };

    const shieldImageOptions = {
        imageLabel: `inActiveEmotionCharacterShield_shield_${emotion.character}`,
        imageWidth: 39,
        imageHeight: 62.4,
        imageUrl: "/images/icons/shield-grit.svg",
        imageCSS: {
            display: `inline-flex`,
            filter: shieldFilterCSS,
            zIndex: 0,
            ...webkitFilterGlitchFix,
            position: "relative" as "relative",
        },
    };

    const characterStyle = {
        fontSize: "24px",
        color: `#eac46b`,
        margin: "13px",
        position: "relative" as "relative",
        bottom: `3px`,
        textShadow: `0px 0px 4px rgba(243, 202, 114, 0.35)`,
    };

    const shieldRotationStyleClass = StyleClass(
        `shieldRotation_${emotion.character}`,
        {
            transform: `rotate(${MathUtils.RandomNumberBetween(-4, 4)}deg)`,
        }
    );

    const inactiveShieldStyleClass = StyleClass(
        `inactiveEmotionCharacterOpacity`,
        {
            opacity: 0.5,
            ":hover": {
                opacity: 0.8,
            },
            ...webkitFilterGlitchFix,
        }
    );

    return (
        <span
            onMouseEnter={() => setBottomPosition(bottomPosition + 3)}
            onMouseLeave={() => setBottomPosition(bottomPosition - 3)}
            style={{
                position: `relative`,
                transition: `all 0.2s ease`,
                bottom: `${bottomPosition}px`,
            }}
        >
            <EmotionCharacterButton
                isActive={false}
                handleClick={handleClick}
                handleLongPress={handleLongPress}
                classes={classNames(
                    shieldRotationStyleClass,
                    inactiveShieldStyleClass
                )}
            >
                <ImageWithOverlay options={imageOptions}>
                    <ImageWithOverlay options={shieldImageOptions} />
                    <span
                        style={{
                            position: `absolute`,
                            inset: 0,
                            display: `inline-flex`,
                            alignItems: `center`,
                            justifyContent: `center`,
                            zIndex: 1,
                            pointerEvents: `none`,
                        }}
                    >
                        <span
                            className={StyleClass(
                                `InactiveEmotionCharacter_${emotion.character}`,
                                characterStyle
                            )}
                            style={
                                useAnopenscrollFont
                                    ? undefined
                                    : Fonts.iMFell
                            }
                        >
                            {emotion.character}
                        </span>
                    </span>
                    <ShieldOrnaments isActive={false} />
                </ImageWithOverlay>
            </EmotionCharacterButton>
        </span>
    );
}
